// Illustration-exercise scenes. The SVG depicts ONE spatial relation (and never
// shows the answer text); the authored `feature` is the form that relation
// entails (motion-toward ⇒ accusative goal, location ⇒ locative, source/from ⇒
// ablative, with-instrument ⇒ instrumental). The engine computes the surface
// from the paradigm and builds distractors from other cells of the same word,
// so the answer is unique AND visually justified.

import type { Scene } from './types';

const INK = '#432c75';
const GOLD = '#b58e4a';
const RULE = '#d5cfc1';
const FRAME =
  '<rect width="900" height="380" fill="#faf7f0"/><rect x="16" y="16" width="868" height="348" fill="none" stroke="' + RULE + '" stroke-width="1.5"/>' +
  '<line x1="60" y1="312" x2="840" y2="312" stroke="#b8b1a0" stroke-width="1.6"/>';

/** A striding figure (left), facing right. */
function figure(): string {
  return (
    `<g stroke="${INK}" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round">` +
    `<circle cx="150" cy="196" r="18" fill="#faf7f0"/>` +
    `<path d="M150 214 L 164 270"/>` +
    `<path d="M154 230 L 182 244"/><path d="M150 228 L 126 250"/>` +
    `<path d="M164 270 L 186 308"/><path d="M164 270 L 144 310"/>` +
    `</g>`
  );
}
/** A standing figure centred at cx (no stride) — for static / location scenes. */
function standingFigure(cx: number): string {
  return (
    `<g stroke="${INK}" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round">` +
    `<circle cx="${cx}" cy="206" r="16" fill="#faf7f0"/><path d="M${cx} 222 L ${cx} 276"/>` +
    `<path d="M${cx} 236 L ${cx - 18} 256"/><path d="M${cx} 236 L ${cx + 18} 256"/>` +
    `<path d="M${cx} 276 L ${cx - 14} 312"/><path d="M${cx} 276 L ${cx + 14} 312"/></g>`
  );
}
/** A gold direction arrow from x1 to x2 at height y (arrowhead at x2). */
function arrow(x1: number, x2: number, y: number): string {
  return (
    `<g stroke="${GOLD}" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round">` +
    `<path d="M${x1} ${y} L ${x2} ${y}"/><path d="M${x2} ${y} L ${x2 - 18} ${y - 12}"/><path d="M${x2} ${y} L ${x2 - 18} ${y + 12}"/></g>`
  );
}
/** A gold arrow pointing LEFT (arrowhead at x2 < x1) — for "away from" / source. */
function arrowFrom(x1: number, x2: number, y: number): string {
  return (
    `<g stroke="${GOLD}" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round">` +
    `<path d="M${x1} ${y} L ${x2} ${y}"/><path d="M${x2} ${y} L ${x2 + 18} ${y - 12}"/><path d="M${x2} ${y} L ${x2 + 18} ${y + 12}"/></g>`
  );
}
/** A śikhara temple centered near cx. */
function temple(cx: number): string {
  const x = cx - 54;
  return (
    `<g fill="none" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round" stroke-linecap="round">` +
    `<rect x="${x}" y="258" width="108" height="54" fill="#faf7f0"/>` +
    `<rect x="${x + 24}" y="184" width="60" height="74" fill="#faf7f0"/>` +
    `<path d="M${x + 30} 258 L ${cx} 120 L ${x + 78} 258 Z" fill="#faf7f0"/>` +
    `<path d="M${cx - 16} 312 L ${cx - 16} 276 Q ${cx} 262 ${cx + 16} 276 L ${cx + 16} 312" fill="#ebe2cc" stroke-width="1.8"/>` +
    `</g><g stroke="${GOLD}" stroke-width="2" fill="${GOLD}"><line x1="${cx}" y1="120" x2="${cx}" y2="100"/><circle cx="${cx}" cy="94" r="6"/></g>`
  );
}
/** Two simple village huts near cx. */
function village(cx: number): string {
  const hut = (hx: number, w: number) =>
    `<g fill="none" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round" stroke-linecap="round">` +
    `<rect x="${hx}" y="262" width="${w}" height="50" fill="#faf7f0"/>` +
    `<path d="M${hx - 8} 262 L ${hx + w / 2} 224 L ${hx + w + 8} 262 Z" fill="#ebe2cc"/></g>`;
  return hut(cx - 70, 64) + hut(cx + 14, 70);
}
/** A small grove of trees near cx (for forest / vana scenes). */
function forest(cx: number): string {
  const tree = (tx: number, h: number) =>
    `<g fill="none" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round" stroke-linecap="round">` +
    `<path d="M${tx} 312 L ${tx} ${312 - h * 0.42}"/>` +
    `<path d="M${tx} ${312 - h} C ${tx - 34} ${312 - h * 0.72}, ${tx - 34} ${312 - h * 0.34}, ${tx} ${312 - h * 0.42} C ${tx + 34} ${312 - h * 0.34}, ${tx + 34} ${312 - h * 0.72}, ${tx} ${312 - h} Z" fill="#ebe2cc"/></g>`;
  return tree(cx - 56, 132) + tree(cx + 6, 158) + tree(cx + 62, 120);
}
/** A garland (mālā): a gold loop with bead dots, hung near cx at top. */
function garland(cx: number): string {
  const beads = Array.from({ length: 9 }, (_, i) => {
    const t = i / 8;
    const bx = cx - 60 + 120 * t;
    const by = 150 + Math.sin(Math.PI * t) * 70;
    return `<circle cx="${bx.toFixed(1)}" cy="${by.toFixed(1)}" r="5"/>`;
  }).join('');
  return (
    `<g stroke="${GOLD}" stroke-width="2.2" fill="none" stroke-linecap="round">` +
    `<path d="M${cx - 60} 150 Q ${cx} 268 ${cx + 60} 150"/></g>` +
    `<g stroke="${GOLD}" stroke-width="1.4" fill="${GOLD}">${beads}</g>`
  );
}
/** A marching column of spears near cx — depicts an army (senā). */
function army(cx: number): string {
  const spear = (sx: number) =>
    `<g stroke="${INK}" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round">` +
    `<line x1="${sx}" y1="312" x2="${sx}" y2="196"/>` +
    `<path d="M${sx} 196 L ${sx - 7} 210 L ${sx + 7} 210 Z" fill="#ebe2cc"/></g>`;
  return spear(cx - 48) + spear(cx - 16) + spear(cx + 16) + spear(cx + 48);
}
function svg(inner: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 380" role="img" preserveAspectRatio="xMidYMid meet">${FRAME}${inner}</svg>`;
}

const GRAMMAR_SCENES: Scene[] = [
  // ----- motion-toward ⇒ accusative goal (…m) --------------------------------
  {
    id: 'sc-toward-temple', bucket: 'scenes',
    svg: svg(figure() + arrow(250, 560, 150) + temple(700)),
    relation: 'motion-toward', paradigmSet: 'nouns', lemmaId: 'mandira', feature: { case: 'acc', number: 'sg' },
    instruction: 'The figure is walking toward the temple. Choose the form of mandira (temple) that names the goal of the motion.',
    gloss: 'Goal of motion takes the accusative: …mandiram gacchati, “goes to the temple”.',
  },
  {
    id: 'sc-toward-village', bucket: 'scenes',
    svg: svg(figure() + arrow(250, 560, 150) + village(700)),
    relation: 'motion-toward', paradigmSet: 'nouns', lemmaId: 'grama', feature: { case: 'acc', number: 'sg' },
    instruction: 'The figure is walking toward the village. Choose the form of grāma (village) that names the goal of the motion.',
    gloss: 'Goal of motion takes the accusative: …grāmam gacchati, “goes to the village”.',
  },
  {
    id: 'sc-toward-forest', bucket: 'scenes',
    svg: svg(figure() + arrow(250, 560, 150) + forest(700)),
    relation: 'motion-toward', paradigmSet: 'nouns', lemmaId: 'vana', feature: { case: 'acc', number: 'sg' },
    instruction: 'The figure is walking toward the forest. Choose the form of vana (forest) that names the goal of the motion.',
    gloss: 'Goal of motion takes the accusative: …vanam gacchati, “goes to the forest”.',
  },

  // ----- location ⇒ locative (…e / …ām) --------------------------------------
  {
    id: 'sc-in-village', bucket: 'scenes',
    // figure standing AMONG the huts (no arrow) — location, not motion.
    svg: svg(village(560) + standingFigure(470)),
    relation: 'location', paradigmSet: 'nouns', lemmaId: 'grama', feature: { case: 'loc', number: 'sg' },
    instruction: 'The figure is standing in the village. Choose the form of grāma (village) that names where he is.',
    gloss: 'Location takes the locative: grāme tiṣṭhati, “stands in the village”.',
  },
  {
    id: 'sc-in-temple', bucket: 'scenes',
    // figure standing INSIDE the temple footprint (no arrow) — location.
    svg: svg(temple(560) + standingFigure(560)),
    relation: 'location', paradigmSet: 'nouns', lemmaId: 'mandira', feature: { case: 'loc', number: 'sg' },
    instruction: 'The figure is standing inside the temple. Choose the form of mandira (temple) that names where he is.',
    gloss: 'Location takes the locative: mandire tiṣṭhati, “stands in the temple”.',
  },

  // ----- source / from ⇒ ablative (…āt) --------------------------------------
  {
    id: 'sc-from-village', bucket: 'scenes',
    // figure on the right, arrow points LEFT (away) — leaving the village behind.
    svg: svg(village(700) + arrowFrom(620, 320, 150) + standingFigure(230)),
    relation: 'source', paradigmSet: 'nouns', lemmaId: 'grama', feature: { case: 'abl', number: 'sg' },
    instruction: 'The figure is moving away from the village. Choose the form of grāma (village) that names the source he leaves.',
    gloss: 'Source / origin of motion takes the ablative: grāmāt gacchati, “goes from the village”.',
  },
  {
    id: 'sc-from-forest', bucket: 'scenes',
    svg: svg(forest(700) + arrowFrom(620, 320, 150) + standingFigure(230)),
    relation: 'source', paradigmSet: 'nouns', lemmaId: 'vana', feature: { case: 'abl', number: 'sg' },
    instruction: 'The figure is moving away from the forest. Choose the form of vana (forest) that names the source he leaves.',
    gloss: 'Source / origin of motion takes the ablative: vanāt gacchati, “goes from the forest”.',
  },

  // ----- with-instrument ⇒ instrumental (…ayā) -------------------------------
  {
    id: 'sc-with-garland', bucket: 'scenes',
    // a standing figure holding up a garland — the garland is the instrument.
    svg: svg(standingFigure(450) + garland(450)),
    relation: 'with-instrument', paradigmSet: 'nouns', lemmaId: 'mala', feature: { case: 'ins', number: 'sg' },
    instruction: 'The figure honours the god WITH a garland. Choose the form of mālā (garland) that names what he uses.',
    gloss: 'The instrument / means takes the instrumental: mālayā pūjayati, “worships with a garland”.',
  },
  {
    id: 'sc-with-army', bucket: 'scenes',
    // the figure advances accompanied BY an army (column of spears).
    svg: svg(figure() + army(560)),
    relation: 'with-instrument', paradigmSet: 'nouns', lemmaId: 'sena', feature: { case: 'ins', number: 'sg' },
    instruction: 'The figure marches WITH an army. Choose the form of senā (army) that names the accompaniment.',
    gloss: 'Accompaniment / means takes the instrumental: senayā gacchati, “goes with an army”.',
  },
];

const SCENE_SETS: Record<string, Scene[]> = {
  scenes: GRAMMAR_SCENES,
  grammar: GRAMMAR_SCENES,
};

export function scenes(key: string): Scene[] {
  return SCENE_SETS[key] ?? [];
}
